import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, FileDown, Loader2, Save, Copy, Trash2, Eye, EyeOff } from "lucide-react";
import api from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PosterForm } from "@/components/posters/PosterForm";
import { PosterPreview } from "@/components/posters/PosterPreview";
import { useUnsavedGuard } from "@/components/posters/useUnsavedGuard";
import { FIELD_META, blobErrorMessage, downloadBlob, filenameFromDisposition } from "@/components/posters/posterUtils";

const PREVIEW_DEBOUNCE_MS = 450;
const btn = "inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-50";
const snapshot = (title, values) => JSON.stringify({ title, values });

function usePosterPreview(templateId, values) {
  const [preview, setPreview] = useState({ image: null, fits: [], ok: true });
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);
  const key = JSON.stringify(values);
  useEffect(() => {
    if (!templateId) return undefined;
    const my = ++seq.current;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/portal/posters/render", { template_id: templateId, values, format: "preview" });
        if (my === seq.current) setPreview({ ...data, forKey: key });
      } catch (e) {
        if (my === seq.current) toast.error(e?.response?.data?.detail || "The preview could not be updated. Your text is safe.");
      } finally {
        if (my === seq.current) setBusy(false);
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [templateId, key]); // eslint-disable-line react-hooks/exhaustive-deps
  return { preview, busy, current: preview.forKey === key };
}

function ConfirmDelete({ open, onOpenChange, onConfirm, title }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="poster-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this poster?</AlertDialogTitle>
          <AlertDialogDescription>&ldquo;{title}&rdquo; will be permanently deleted. Downloaded files are not affected.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="poster-delete-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} data-testid="poster-delete-confirm" className="bg-red-600 hover:bg-red-700">Delete poster</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function PortalPosterEditorPage() {
  const { posterId, templateId: newTemplateId } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [poster, setPoster] = useState(null); // saved record (id + revision)
  const [title, setTitle] = useState("");
  const [values, setValues] = useState(null);
  const [saved, setSaved] = useState(null);
  const [notes, setNotes] = useState({});
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState("");
  const [guides, setGuides] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [jpgInfo, setJpgInfo] = useState("");

  const dirty = values != null && snapshot(title, values) !== saved;
  const { dialog, guardedNavigate } = useUnsavedGuard(dirty);
  const { preview, busy, current } = usePosterPreview(template?.id, values);

  const loadDefaults = useCallback(async () => {
    const { data } = await api.get("/portal/posters/defaults");
    setNotes(data.notes || {});
    return data.values;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        if (posterId) {
          const { data } = await api.get(`/portal/posters/${posterId}`);
          setTemplate(data.template); setPoster(data); setTitle(data.title); setValues(data.values);
          setSaved(snapshot(data.title, data.values));
          loadDefaults().catch(() => {});
        } else {
          const [{ data: list }, defaults] = await Promise.all([api.get("/portal/posters/templates"), loadDefaults()]);
          const t = (list.items || []).find((x) => x.id === newTemplateId);
          if (!t) { setLoadError("That template is no longer available."); return; }
          const v = { ...defaults, description: t.default_description || "" };
          setTemplate(t); setTitle(`${t.title} poster`); setValues(v); setSaved(snapshot(`${t.title} poster`, v));
        }
      } catch (e) {
        setLoadError(e?.response?.data?.detail || "This poster could not be loaded.");
      }
    })();
  }, [posterId, newTemplateId, loadDefaults]);

  const setField = (k, v) => setValues((cur) => ({ ...cur, [k]: v }));
  const resetFranchise = async () => {
    try {
      const d = await loadDefaults();
      setValues((cur) => ({ ...cur, franchise: d.franchise, name: d.name, email: d.email, phone: d.phone }));
    } catch { toast.error("Could not load your franchise details."); }
  };
  const resetDescription = () => setField("description", template?.default_description || "");

  const save = async () => {
    setSaving(true);
    try {
      if (poster) {
        const { data } = await api.put(`/portal/posters/${poster.id}`, { title, values, revision: poster.revision });
        setPoster(data); setTitle(data.title); setSaved(snapshot(data.title, values));
        toast.success("Poster saved");
      } else {
        const { data } = await api.post("/portal/posters", { title, template_id: template.id, values });
        setSaved(snapshot(data.title, values));
        toast.success("Poster saved");
        navigate(`/portal/posters/${data.id}`, { replace: true });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The poster could not be saved. Your text is still here, please try again.");
    } finally { setSaving(false); }
  };

  const saveCopy = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/portal/posters", { title: `Copy of ${title}`.slice(0, 120), template_id: template.id, values });
      setSaved(snapshot(title, values)); // leaving this poster: the copy now holds the edits
      toast.success("Saved as a new copy");
      navigate(`/portal/posters/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The copy could not be saved. Your text is still here.");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/portal/posters/${poster.id}`);
      setSaved(snapshot(title, values));
      toast.success("Poster deleted");
      navigate("/portal/posters");
    } catch (e) { toast.error(e?.response?.data?.detail || "The poster could not be deleted."); }
  };

  const exportAs = async (format) => {
    setExporting(format); setJpgInfo("");
    try {
      const exportId = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      const res = await api.post("/portal/posters/render", { template_id: template.id, values, format, title, export_id: exportId }, { responseType: "blob" });
      downloadBlob(res.data, filenameFromDisposition(res.headers, `poster.${format}`));
      if (format === "jpg") {
        const kb = Math.round(Number(res.headers["x-image-bytes"] || res.data.size) / 1024);
        setJpgInfo(`JPG downloaded: ${res.headers["x-image-width"] || "?"}px wide, ${kb} KB${kb > 220 ? " (kept above our target size to stay readable)" : ""}.`);
      }
    } catch (e) {
      toast.error(await blobErrorMessage(e, "The download failed. Your text is still here, please try again."));
    } finally { setExporting(""); }
  };

  const blocked = current && !preview.ok;
  const fields = useMemo(() => FIELD_META, []);

  if (loadError) return <div className="px-6 md:px-10 py-8 text-sm text-red-700" data-testid="poster-load-error">{loadError}</div>;
  if (!values || !template) return <div className="px-6 md:px-10 py-8 text-stone-400"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="px-4 md:px-10 py-8 max-w-7xl mx-auto" data-testid="poster-editor-page">
      {dialog}
      <button type="button" onClick={() => guardedNavigate("/portal/posters")} data-testid="poster-back"
        className="inline-flex items-center gap-1 text-xs font-semibold text-stone-600 hover:text-stone-950 mb-3 transition-colors">
        <ArrowLeft className="w-3.5 h-3.5" /> Poster Studio
      </button>
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end justify-between gap-4 mb-6">
        <div className="min-w-0 w-full sm:w-auto sm:flex-1">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">{template.title}{template.status !== "published" ? " · older version" : ""}</div>
          <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="poster-title" aria-label="Poster name"
            className="font-display text-3xl md:text-4xl font-black text-stone-950 bg-transparent border-b border-transparent hover:border-stone-300 focus:border-stone-950 focus:outline-none w-full mt-1" />
          {dirty && <div className="text-xs text-amber-700 mt-1" data-testid="poster-dirty">Unsaved changes</div>}
          {!poster && !dirty && <div className="text-xs text-stone-500 mt-1" data-testid="poster-new-hint">New poster, not saved yet</div>}
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={save} disabled={saving} data-testid="poster-save" className={`${btn} bg-stone-950 text-[#dddd16] hover:bg-stone-800`}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} {poster ? "Save" : "Save poster"}
          </button>
          {poster && (
            <>
              <button type="button" onClick={saveCopy} disabled={saving} data-testid="poster-save-copy" className={`${btn} border border-stone-300 text-stone-800 hover:border-stone-950`}>
                <Copy className="w-3.5 h-3.5" /> Save a copy
              </button>
              <button type="button" onClick={() => setConfirmDelete(true)} data-testid="poster-delete" className={`${btn} border border-stone-300 text-red-700 hover:border-red-600`}>
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] gap-6 lg:gap-8 items-start">
        <div className="space-y-4 order-2 lg:order-1">
          <PosterForm fields={fields} values={values} setField={setField} fits={current ? preview.fits : []} notes={notes}
            onResetFranchise={resetFranchise} onResetDescription={resetDescription} hasDefaultDescription={!!template.default_description} />
          <section className="bg-white border border-stone-200 rounded-xl p-5" data-testid="poster-downloads">
            <h2 className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-3">Download</h2>
            {blocked && <p className="text-xs text-red-700 mb-3" data-testid="poster-export-blocked">Please shorten the highlighted text before downloading.</p>}
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => exportAs("pdf")} disabled={!!exporting || blocked} data-testid="poster-download-pdf" className={`${btn} bg-[#dddd16] text-stone-950 hover:bg-[#c9c912]`}>
                {exporting === "pdf" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} PDF for printing (A4)
              </button>
              <button type="button" onClick={() => exportAs("jpg")} disabled={!!exporting || blocked} data-testid="poster-download-jpg" className={`${btn} border border-stone-300 text-stone-800 hover:border-stone-950`}>
                {exporting === "jpg" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} JPG for social / web
              </button>
            </div>
            <p className="text-[11px] text-stone-500 mt-2">Downloads use the text on screen now, including unsaved edits. Downloading doesn&apos;t save the poster.</p>
            {jpgInfo && <p className="text-[11px] text-stone-600 mt-1" data-testid="poster-jpg-info">{jpgInfo}</p>}
          </section>
        </div>
        <div className="order-1 lg:order-2 lg:sticky lg:top-6 space-y-2">
          <PosterPreview image={preview.image} layout={template.layout} guides={guides} busy={busy} fits={current ? preview.fits : []} />
          <button type="button" onClick={() => setGuides((g) => !g)} data-testid="poster-toggle-guides"
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-stone-500 hover:text-stone-950 transition-colors">
            {guides ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />} {guides ? "Hide" : "Show"} text areas (on screen only)
          </button>
        </div>
      </div>
      <ConfirmDelete open={confirmDelete} onOpenChange={setConfirmDelete} onConfirm={remove} title={title} />
    </div>
  );
}
